import { useContext, useEffect, useMemo, useState } from 'react';
import {
	Activity,
	AlertTriangle,
	BarChart3,
	CalendarClock,
	CheckCircle2,
	HelpCircle,
	MapPin,
	Minus,
	Percent,
	Smartphone,
	TrendingDown,
	TrendingUp,
	UserRound,
	type LucideIcon
} from 'lucide-react';
import { GlobalContext } from '../context/GlobalContext';
import { ContainerSpinner } from '../components/Spinner';
import { EmptyState } from '../components/EmptyState';
import { cn } from '../shadcn/utils';
import type { UserData } from '../hooks/auth';
import {
	callAPI,
	type API,
	type StoreHealthBreakdown,
	type StoreHealthHistoryPoint,
	type StoreHealthSnapshot,
	type StoreHealthStatus
} from '../hooks/api';

type HealthStatus = StoreHealthStatus;
type StoreHealth = StoreHealthSnapshot;
type StoreHealthResponse = API['https://api.krece.app/storeprofile/store_health/']['GET']['response'];

type BreakdownTab = 'model' | 'seller' | 'branch';
type TrendRange = 7 | 15 | 30 | 180 | 365 | 'all';

const STATUS_COPY: Record<HealthStatus, { label: string; description: string; Icon: LucideIcon; className: string }> = {
	GOOD: {
		label: 'Excelente',
		description: 'La salud de la tienda está dentro del rango saludable del país.',
		Icon: CheckCircle2,
		className: 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/20 light:text-emerald-700'
	},
	NEUTRAL: {
		label: 'Atención',
		description: 'La tienda está cerca del límite saludable. Conviene revisar vendedores y sucursales con mayor mora.',
		Icon: AlertTriangle,
		className: 'bg-amber-500/10 text-amber-400 ring-amber-500/20 light:text-amber-700'
	},
	BAD: {
		label: 'Crítico',
		description: 'La salud de la tienda supera el umbral del país y requiere gestión activa.',
		Icon: AlertTriangle,
		className: 'bg-red-500/10 text-red-400 ring-red-500/20 light:text-red-700'
	},
	CRITICAL: {
		label: 'Muy crítico',
		description: 'La salud de la tienda está muy por encima del umbral del país. Priorice cobranza y revisión operativa.',
		Icon: AlertTriangle,
		className: 'bg-red-500/10 text-red-400 ring-red-500/20 light:text-red-700'
	},
	NO_DATA: {
		label: 'Sin datos',
		description: 'Todavía no hay suficientes cuotas con 15 días desde su vencimiento para calcular salud.',
		Icon: HelpCircle,
		className: 'bg-neutral-800 text-neutral-300 ring-white/[0.04]'
	}
};
type HistoryPeriod = '30d' | '6m' | '1y' | 'max';

const BREAKDOWN_TABS: { id: BreakdownTab; label: string; Icon: LucideIcon }[] = [
	{ id: 'model', label: 'Modelo', Icon: Smartphone },
	{ id: 'seller', label: 'Vendedor', Icon: UserRound },
	{ id: 'branch', label: 'Sucursal', Icon: MapPin }
];

const BREAKDOWN_DIMENSION_LABEL: Record<BreakdownTab, string> = {
	model: 'Modelo',
	seller: 'Vendedor',
	branch: 'Sucursal'
};

const TREND_RANGE_OPTIONS: { value: TrendRange; label: string }[] = [
	{ value: 7, label: '7d' },
	{ value: 15, label: '15d' },
	{ value: 30, label: '30d' },
	{ value: 180, label: '6M' },
	{ value: 365, label: '1A' },
	{ value: 'all', label: 'Todo' }
];

function getHistoryPeriod(range: TrendRange): HistoryPeriod {
	if (range === 180) return '6m';
	if (range === 365) return '1y';
	if (range === 'all') return 'max';
	return '30d';
}

export default function Salud() {
	const { userData, internalCountry } = useContext(GlobalContext);
	const [selectedBreakdown, setSelectedBreakdown] = useState<BreakdownTab>('model');
	const [selectedTrendRange, setSelectedTrendRange] = useState<TrendRange>(15);
	const [storeHealthData, setStoreHealthData] = useState<StoreHealthResponse | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<boolean | string>(false);
	const [fetched, setFetched] = useState(false);
	const [healthRequest, setHealthRequest] = useState(0);
	const requestedHistoryPeriod = getHistoryPeriod(selectedTrendRange);
	const internalCountryValue = internalCountry.get();

	const storeEmail = userData?.master_store?.email || userData?.master_store?.id || userData?.email;
	const fallbackHealth = useMemo(() => buildFallbackHealth(userData?.master_store || userData, storeEmail), [storeEmail, userData]);
	const refetchHealth = () => setHealthRequest((value) => value + 1);

	useEffect(() => {
		let cancelled = false;
		if (!storeEmail) {
			setLoading(false);
			return;
		}

		setLoading(true);
		setError(false);
		setFetched(false);
		void callAPI(
			'https://api.krece.app/storeprofile/store_health/',
			'GET',
			{ history_period: requestedHistoryPeriod },
			userData,
			internalCountryValue,
			true
		).then(([data, requestError]) => {
			if (cancelled) return;
			if (requestError) setError(requestError);
			else setStoreHealthData(data);
			setLoading(false);
			setFetched(true);
		});

		return () => {
			cancelled = true;
		};
	}, [healthRequest, internalCountryValue, requestedHistoryPeriod, storeEmail, userData]);

	const health = storeHealthData?.health;
	const history = useMemo(() => storeHealthData?.history || [], [storeHealthData?.history]);
	const displayHealth = health || (fetched && !error ? fallbackHealth : null);

	const breakdownRows = useMemo(() => {
		if (!displayHealth) return [];
		if (selectedBreakdown === 'model') return displayHealth.by_model || [];
		if (selectedBreakdown === 'seller') return displayHealth.by_seller || [];
		return displayHealth.by_branch || [];
	}, [displayHealth, selectedBreakdown]);

	if ((!fetched || loading) && !health) return <ContainerSpinner />;

	if (error && !health) {
		return (
			<div className="p-4 md:p-6">
				<EmptyState
					icon={AlertTriangle}
					title="No pudimos cargar la salud"
					subtitle="Intenta de nuevo en unos minutos. La tienda puede tener un corte disponible aunque no se muestre ahora."
				/>
			</div>
		);
	}

	if (!displayHealth) {
		return (
			<div className="p-4 md:p-6">
				<EmptyState
					icon={Activity}
					title="Salud no disponible"
					subtitle="La data diaria de salud todavía no existe para esta tienda. Intenta de nuevo después del próximo corte."
				/>
			</div>
		);
	}

	const healthData = displayHealth;
	const hasCurrentThresholds = healthData.health.country_thresholds?.window_days === 15;
	const thresholdLow = hasCurrentThresholds ? toNumber(healthData.health.country_thresholds?.low) : null;
	const thresholdHigh = hasCurrentThresholds ? toNumber(healthData.health.country_thresholds?.high) : null;
	const statusKey = getHealthStatus(healthData);
	const isStaleSnapshot = !healthData.is_legacy_fallback && !hasCurrentThresholds;
	const status = healthData.is_legacy_fallback
		? { ...STATUS_COPY.NO_DATA, label: 'Primer corte pendiente' }
		: isStaleSnapshot
			? { ...STATUS_COPY.NO_DATA, label: 'Actualización pendiente' }
			: STATUS_COPY[statusKey];
	const interventions = getInterventions(healthData);
	const delta7d = getDeltaSince(getSortedHistory(history), 'delinquency_15d', 7);

	return (
		<div className="min-h-full space-y-4 p-4 md:p-6">
			<section className="rounded-2xl border border-neutral-800 bg-neutral-925 p-5 shadow-sm md:p-6">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
					<div>
						<p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-500">
							{healthData.is_legacy_fallback
								? 'Primer corte pendiente · salud 15d'
								: `Corte ${formatDate(healthData.as_of_date)} · últimos ${healthData.lookback_days || 180} días · mora 15d por cuota`}
						</p>
						<h1 className="mt-1 text-2xl font-bold tracking-tight text-neutral-0 md:text-3xl">
							{healthData.store_name || healthData.store_email}
						</h1>
					</div>
					<span className={cn('inline-flex w-fit items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold ring-1', status.className)}>
						<span className="size-2 rounded-full bg-current" />
						{status.label}
					</span>
				</div>

				<HealthBand rate={healthData.metrics.delinquency_15d} low={thresholdLow} high={thresholdHigh} />
				<Recommendation health={healthData} status={statusKey} thresholdLow={thresholdLow} interventions={interventions} />
			</section>

			<section className="grid grid-cols-1 gap-4 md:grid-cols-3">
				<KpiCard
					label="Mora 15d"
					value={formatPct(healthData.metrics.delinquency_15d)}
					detail={
						healthData.is_legacy_fallback
							? 'El detalle de cuotas estará disponible con el primer corte diario'
							: `${formatNumber(healthData.metrics.delinquent_15d)} de ${formatNumber(healthData.metrics.evaluable_15d)} cuotas seguían impagas a los 15 días`
					}
					Icon={Percent}
				/>
				<KpiCard
					label="Cambio en 7 días"
					value={formatDeltaPp(delta7d)}
					detail={getDeltaDetail(delta7d, healthData.metrics.delinquency_15d)}
					Icon={delta7d !== null && delta7d < 0 ? TrendingDown : TrendingUp}
					tone={delta7d === null ? undefined : delta7d > 0 ? 'bad' : delta7d < 0 ? 'good' : undefined}
				/>
				<KpiCard
					label="Cuotas con 15d"
					value={formatNumber(healthData.metrics.evaluable_15d)}
					detail={
						healthData.is_legacy_fallback
							? 'Disponible cuando se genere el primer corte diario'
							: 'Cuotas que ya cumplieron 15 días desde su vencimiento'
					}
					Icon={CalendarClock}
				/>
			</section>

			<TrendChart
				history={history}
				selectedRange={selectedTrendRange}
				onRangeChange={setSelectedTrendRange}
				loading={loading}
				error={error}
				onRetry={refetchHealth}
			/>

			<section className="grid grid-cols-1 gap-4 xl:grid-cols-[1.35fr_1fr]">
				<div className="rounded-2xl border border-neutral-800 bg-neutral-925 p-5 shadow-sm md:p-6">
					<p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-500">Acción</p>
					<h2 className="mt-1 text-lg font-bold text-neutral-0">¿Dónde intervenir primero?</h2>
					{interventions.length ? (
						<ol className="mt-4 space-y-2.5">
							{interventions.map((item, index) => (
								<InterventionRow key={`${item.dimension}-${item.row.label}`} rank={index + 1} item={item} />
							))}
						</ol>
					) : (
						<div className="mt-4 rounded-xl bg-neutral-900 px-4 py-6 text-sm text-neutral-500 ring-1 ring-inset ring-white/[0.04]">
							No hay focos fuertes con la muestra actual.
						</div>
					)}
				</div>

				<div className="rounded-2xl border border-neutral-800 bg-neutral-925 p-5 shadow-sm md:p-6">
					<p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-500">Movimiento · 7 días</p>
					<h2 className="mt-1 text-lg font-bold text-neutral-0">Qué cambió</h2>
					<div className="mt-4 space-y-2.5">
						<MoveList health={healthData} history={history} />
					</div>
				</div>
			</section>

			<section className="rounded-2xl border border-neutral-800 bg-neutral-925 p-5 shadow-sm md:p-6">
				<div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
					<div>
						<p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-500">Cartera</p>
						<h2 className="mt-1 text-lg font-bold text-neutral-0">Dónde se concentra la mora 15d</h2>
					</div>
					<div className="grid grid-cols-3 overflow-hidden rounded-full border border-neutral-800 bg-neutral-900 p-1">
						{BREAKDOWN_TABS.map(({ id, label, Icon }) => (
							<button
								key={id}
								onClick={() => setSelectedBreakdown(id)}
								className={cn(
									'flex min-w-0 items-center justify-center gap-1.5 rounded-full px-1 py-2 text-xs font-semibold text-neutral-400 transition-colors sm:px-4',
									selectedBreakdown === id && 'bg-primary-500/15 text-primary-400 light:text-primary-700'
								)}
							>
								<Icon className="hidden size-3.5 flex-none sm:block" />
								<span className="truncate">{label}</span>
							</button>
						))}
					</div>
				</div>

				<BreakdownTable rows={breakdownRows} />
				<p className="mt-4 text-xs text-neutral-500">
					“Cuotas en mora” muestra X de Y: X seguían impagas al cumplir 15 días de vencidas; Y ya habían cumplido 15 días y podían
					medirse. Se consideran los últimos {healthData.lookback_days || 180} días del corte.
				</p>
			</section>
		</div>
	);
}

function HealthBand({ rate, low, high }: { rate: number | null; low: number | null; high: number | null }) {
	if (rate === null || low === null || high === null || low < 0 || high <= low) return null;

	const markerLeft = getBandPosition(rate, low, high);

	return (
		<div className="mt-9">
			<div className="relative flex h-3 w-full">
				<div className="h-full w-1/2 rounded-l-full bg-emerald-500/20" />
				<div className="h-full w-[16.7%] bg-amber-500/25" />
				<div className="h-full flex-1 rounded-r-full bg-red-500/20" />
				<div className="absolute -top-1.5 z-10 -translate-x-1/2" style={{ left: `${markerLeft}%` }}>
					<div
						className={cn(
							'absolute -top-7 whitespace-nowrap rounded-md bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-950',
							markerLeft <= 10 ? 'left-1/2' : markerLeft >= 90 ? 'right-1/2' : 'left-1/2 -translate-x-1/2'
						)}
					>
						Mora 15d · {formatPct(rate)}
					</div>
					<div className="h-6 w-1 rounded-full bg-neutral-100 shadow-[0_0_0_3px] shadow-neutral-925" />
				</div>
			</div>
			<div className="mt-3 grid grid-cols-3 gap-2 text-[11px] font-medium text-neutral-400">
				<span>
					<b className="text-neutral-200">Saludable</b> · &le;{formatThreshold(low)}%
				</span>
				<span className="text-center">
					<b className="text-neutral-200">Atención</b> · &gt;{formatThreshold(low)}% y &lt;{formatThreshold(high)}%
				</span>
				<span className="text-right">
					<b className="text-neutral-200">Crítico</b> · &ge;{formatThreshold(high)}%
				</span>
			</div>
			<p className="mt-2 text-[11px] text-neutral-500">Umbrales de mora 15d configurados por país; definen el estado de salud.</p>
		</div>
	);
}

function getBandPosition(rate: number, low: number, high: number) {
	if (rate <= low) return low === 0 ? 0 : (rate / low) * 50;
	if (rate <= high) return 50 + ((rate - low) / (high - low)) * 16.7;
	return 66.7 + Math.min((rate - high) / high, 1) * 33.3;
}

function Recommendation({
	health,
	status,
	thresholdLow,
	interventions
}: {
	health: StoreHealth;
	status: HealthStatus;
	thresholdLow: number | null;
	interventions: Intervention[];
}) {
	const copy = STATUS_COPY[status];
	const toneClass =
		status === 'GOOD'
			? 'border-emerald-500/20 bg-emerald-500/[0.05]'
			: status === 'NEUTRAL'
				? 'border-amber-500/20 bg-amber-500/[0.05]'
				: status === 'NO_DATA'
					? 'border-neutral-800 bg-neutral-900'
					: 'border-red-500/20 bg-red-500/[0.05]';
	const iconClass =
		status === 'GOOD'
			? 'text-emerald-400 light:text-emerald-700'
			: status === 'NEUTRAL'
				? 'text-amber-400 light:text-amber-700'
				: status === 'NO_DATA'
					? 'text-neutral-500'
					: 'text-red-400 light:text-red-700';
	const Icon = copy.Icon;
	const focusItems = interventions.slice(0, 2);

	if (health.is_legacy_fallback) {
		return (
			<div className="mt-5 flex items-start gap-3 rounded-xl border border-neutral-800 bg-neutral-900 px-4 py-3">
				<HelpCircle className="mt-0.5 size-5 flex-none text-neutral-500" />
				<p className="text-sm text-neutral-300">
					<b className="font-semibold text-neutral-100">{health.store_name || 'La tienda'} espera su primer corte diario.</b>{' '}
					La mora 15d mostrada es referencial. Los conteos y el desglose por modelo, vendedor y sucursal aparecerán con ese corte.
				</p>
			</div>
		);
	}

	if (health.health.country_thresholds?.window_days !== 15) {
		return (
			<div className="mt-5 flex items-start gap-3 rounded-xl border border-neutral-800 bg-neutral-900 px-4 py-3">
				<HelpCircle className="mt-0.5 size-5 flex-none text-neutral-500" />
				<p className="text-sm text-neutral-300">
					<b className="font-semibold text-neutral-100">{health.store_name || 'La tienda'} espera un corte actualizado.</b>{' '}
					La mora 15d ya está visible, pero la zona y sus umbrales aparecerán cuando se genere el próximo corte diario.
				</p>
			</div>
		);
	}

	return (
		<div className={cn('mt-5 flex items-start gap-3 rounded-xl border px-4 py-3', toneClass)}>
			<Icon className={cn('mt-0.5 size-5 flex-none', iconClass)} />
			<p className="text-sm text-neutral-300">
				<b className="font-semibold text-neutral-100">
					{health.store_name || 'La tienda'} está en {getZoneLabel(status)}.
				</b>{' '}
				{status === 'GOOD' && thresholdLow !== null
					? `La mora 15d (${formatPct(health.metrics.delinquency_15d)}) está dentro del rango saludable del país (hasta ${formatThreshold(thresholdLow)}%).`
					: copy.description}
				{focusItems.length > 0 && (
					<>
						{' '}
						{status === 'GOOD' ? 'Aún así, los focos con más mora 15d son' : 'Empieza por'}{' '}
						{focusItems.map((item, index) => (
							<span key={`${item.dimension}-${item.row.label}`}>
								{index > 0 && ' y '}
								{BREAKDOWN_DIMENSION_LABEL[item.dimension].toLowerCase()}{' '}
								<b className="font-semibold text-neutral-100">{item.row.label}</b> ({formatPct(item.row.delinquency_15d)})
							</span>
						))}
						.
					</>
				)}
				{health.health.low_sample && ' La muestra es pequeña; úsala como señal direccional.'}
				{health.is_legacy_fallback && ' El desglose por modelo, vendedor y sucursal aparecerá cuando exista el corte diario.'}
			</p>
		</div>
	);
}

function getZoneLabel(status: HealthStatus) {
	if (status === 'GOOD') return 'zona saludable';
	if (status === 'NEUTRAL') return 'zona de atención';
	if (status === 'NO_DATA') return 'zona sin datos suficientes';
	return 'zona crítica';
}

function KpiCard({
	label,
	value,
	detail,
	Icon,
	tone
}: {
	label: string;
	value: string;
	detail: string;
	Icon: LucideIcon;
	tone?: 'good' | 'bad';
}) {
	return (
		<div className="relative overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-925 p-5 shadow-sm">
			<div className="flex items-center justify-between gap-2">
				<p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">{label}</p>
				<span className="grid size-9 flex-none place-items-center rounded-xl bg-primary-500/10 text-primary-400 light:text-primary-700">
					<Icon className="size-[18px]" />
				</span>
			</div>
			<p
				className={cn(
					'mt-3 text-3xl font-extrabold tracking-tight text-neutral-0',
					tone === 'bad' && 'text-amber-400 light:text-amber-700',
					tone === 'good' && 'text-emerald-400 light:text-emerald-700'
				)}
			>
				{value}
			</p>
			<p className="mt-2 text-xs text-neutral-500">{detail}</p>
		</div>
	);
}

type Intervention = { dimension: BreakdownTab; row: StoreHealthBreakdown };

function getInterventions(health: StoreHealth): Intervention[] {
	const candidates: Intervention[] = [];
	const dimensions: { dimension: BreakdownTab; rows?: StoreHealthBreakdown[] }[] = [
		{ dimension: 'branch', rows: health.by_branch },
		{ dimension: 'seller', rows: health.by_seller },
		{ dimension: 'model', rows: health.by_model }
	];

	for (const { dimension, rows } of dimensions) {
		const top = (rows || [])
			.filter((row) => row.evaluable_15d >= 3 && row.delinquency_15d !== null && row.delinquency_15d !== undefined && row.delinquency_15d >= 10)
			.sort((a, b) => getBreakdownPriority(b) - getBreakdownPriority(a))[0];
		if (top) candidates.push({ dimension, row: top });
	}

	return candidates.sort((a, b) => getBreakdownPriority(b.row) - getBreakdownPriority(a.row)).slice(0, 3);
}

function InterventionRow({ rank, item }: { rank: number; item: Intervention }) {
	const rate = item.row.delinquency_15d || 0;
	const severe = rate >= 20;

	return (
		<li className="grid grid-cols-[auto_1fr_auto] items-center gap-3.5 rounded-xl border border-neutral-800 bg-neutral-900 px-4 py-3 transition-colors hover:border-neutral-700">
			<span
				className={cn(
					'grid size-7 place-items-center rounded-lg text-xs font-bold text-neutral-950',
					severe ? 'bg-red-400 light:bg-red-700' : 'bg-amber-400 light:bg-amber-700'
				)}
			>
				{rank}
			</span>
			<div className="min-w-0">
				<p className="text-[10px] font-semibold uppercase tracking-[0.05em] text-neutral-500">{BREAKDOWN_DIMENSION_LABEL[item.dimension]}</p>
				<p className="truncate text-sm font-semibold text-neutral-100" title={item.row.label}>
					{item.row.label}
				</p>
				<div className="mt-1.5 h-1 max-w-[210px] overflow-hidden rounded-full bg-neutral-800">
					<div
						className={cn('h-full rounded-full', severe ? 'bg-red-400 light:bg-red-700' : 'bg-amber-400 light:bg-amber-700')}
						style={{ width: `${Math.min(rate, 100)}%` }}
					/>
				</div>
				<p className="mt-1.5 text-[11px] text-neutral-500">
					{formatNumber(item.row.delinquent_15d)} de {formatNumber(item.row.evaluable_15d)} cuotas seguían impagas a los 15 días
				</p>
			</div>
			<div className="text-right">
				<p className={cn('text-lg font-bold tracking-tight', severe ? 'text-red-400 light:text-red-700' : 'text-amber-400 light:text-amber-700')}>
					{formatPct(item.row.delinquency_15d)}
				</p>
				<p className="text-[10px] text-neutral-500">mora 15d</p>
			</div>
		</li>
	);
}

function MoveList({ health, history }: { health: StoreHealth; history: StoreHealthHistoryPoint[] }) {
	const changes = getRecentChanges(health, history);
	const worsened = changes.filter((change) => change.delta > 0.1);
	const improved = changes.filter((change) => change.delta < -0.1);

	if (!worsened.length && !improved.length) {
		return (
			<div className="flex items-start gap-3 rounded-xl border border-neutral-800 bg-neutral-900 px-4 py-3">
				<span className="grid size-8 flex-none place-items-center rounded-lg bg-neutral-800 text-neutral-400">
					<Minus className="size-4" />
				</span>
				<div>
					<p className="text-sm font-semibold text-neutral-100">Sin cambios fuertes</p>
					<p className="mt-0.5 text-xs text-neutral-500">La mora se mantuvo estable en los últimos cortes.</p>
				</div>
			</div>
		);
	}

	return (
		<>
			{worsened.map((change) => (
				<div key={`w-${change.label}`} className="flex items-start gap-3 rounded-xl border border-red-500/20 bg-red-500/[0.05] px-4 py-3">
					<span className="grid size-8 flex-none place-items-center rounded-lg bg-neutral-925 text-red-400 light:text-red-700">
						<TrendingUp className="size-4" />
					</span>
					<div>
						<p className="text-sm font-semibold text-neutral-100">Empeoró · {change.label}</p>
						<p className="mt-0.5 text-xs text-neutral-400">
							<span className="font-semibold text-red-400 light:text-red-700">{formatDeltaPp(change.delta)}</span> · {formatPct(change.previous)} →{' '}
							{formatPct(change.current)} vs {change.comparisonLabel}
						</p>
					</div>
				</div>
			))}
			{improved.map((change) => (
				<div key={`i-${change.label}`} className="flex items-start gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.05] px-4 py-3">
					<span className="grid size-8 flex-none place-items-center rounded-lg bg-neutral-925 text-emerald-400 light:text-emerald-700">
						<TrendingDown className="size-4" />
					</span>
					<div>
						<p className="text-sm font-semibold text-neutral-100">Mejoró · {change.label}</p>
						<p className="mt-0.5 text-xs text-neutral-400">
							<span className="font-semibold text-emerald-400 light:text-emerald-700">{formatDeltaPp(change.delta)}</span> · {formatPct(change.previous)} →{' '}
							{formatPct(change.current)} vs {change.comparisonLabel}
						</p>
					</div>
				</div>
			))}
		</>
	);
}

function TrendChart({
	history,
	selectedRange,
	onRangeChange,
	loading,
	error,
	onRetry
}: {
	history: StoreHealthHistoryPoint[];
	selectedRange: TrendRange;
	onRangeChange: (range: TrendRange) => void;
	loading: boolean;
	error: boolean | string;
	onRetry: () => void;
}) {
	const [hoveredDate, setHoveredDate] = useState<string | null>(null);
	const sortedHistory = getSortedHistory(history);
	const filteredHistory = getFilteredHistory(sortedHistory, selectedRange);
	const bounds = getTrendBounds(filteredHistory, 'delinquency_15d');
	const points15 = buildTimedLinePoints(filteredHistory, 'delinquency_15d', bounds);
	const xLabels = getXAxisLabels(filteredHistory);
	const summary = getTrendSummary(filteredHistory, sortedHistory);
	const hoveredPoint = points15.find((point) => point.date === hoveredDate);
	const hasTrend = filteredHistory.length > 1 && points15.length > 1;

	return (
		<div className="rounded-2xl border border-neutral-800 bg-neutral-925 p-5 shadow-sm md:p-6">
			<div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-500">Tendencia</p>
					<h2 className="mt-1 text-lg font-bold text-neutral-0">
						{summary ? `Mora 15d · ${formatShortDate(summary.firstDate)}–${formatShortDate(summary.latestDate)}` : 'Mora 15d'}
					</h2>
				</div>
				<div className="flex flex-col items-start gap-1.5 lg:items-end">
					<div className="grid w-full grid-cols-6 overflow-hidden rounded-full border border-neutral-800 bg-neutral-900 p-1 lg:w-auto" aria-label="Período de la tendencia">
						{TREND_RANGE_OPTIONS.map((option) => (
							<button
								key={option.label}
								onClick={() => onRangeChange(option.value)}
								className={cn(
									'rounded-full px-1 py-1.5 text-xs font-semibold text-neutral-400 transition-colors sm:px-3',
									selectedRange === option.value && 'bg-primary-500/15 text-primary-400 light:text-primary-700'
								)}
							>
								{option.label}
							</button>
						))}
					</div>
					{loading && <span className="pr-1 text-[11px] text-neutral-500">Actualizando histórico…</span>}
				</div>
			</div>

			{error ? (
				<div className="flex h-56 flex-col items-center justify-center rounded-xl bg-neutral-900 px-4 text-center ring-1 ring-inset ring-white/[0.04]">
					<AlertTriangle className="mb-3 size-8 text-amber-400 light:text-amber-700" />
					<p className="text-sm font-semibold text-neutral-200">No pudimos actualizar este histórico</p>
					<p className="mt-1 max-w-sm text-xs text-neutral-500">El período anterior no reemplazará esta selección.</p>
					<button
						type="button"
						onClick={onRetry}
						className="mt-4 rounded-lg bg-primary-500/15 px-3 py-2 text-xs font-semibold text-primary-400 light:text-primary-700"
					>
						Reintentar
					</button>
				</div>
			) : hasTrend && summary ? (
				<div className="space-y-4">
					<div className="flex flex-wrap gap-x-8 gap-y-3">
						<TrendStat label={`Último 15d · corte ${formatShortDate(summary.latestDate)}`} value={formatPct(summary.latest15)} />
						<TrendStat
							label="Cambio 7d"
							value={formatDeltaPp(summary.delta7d15)}
							tone={summary.delta7d15 === null ? undefined : summary.delta7d15 > 0 ? 'bad' : summary.delta7d15 < 0 ? 'good' : undefined}
						/>
					</div>

					<svg viewBox="0 0 700 260" className="h-40 w-full overflow-visible md:h-64" onPointerLeave={() => setHoveredDate(null)}>
						<defs>
							<linearGradient id="salud-trend-fill" x1="0" y1="0" x2="0" y2="1">
								<stop offset="0" stopColor="currentColor" stopOpacity="0.16" className="text-primary-400 light:text-primary-700" />
								<stop offset="1" stopColor="currentColor" stopOpacity="0" className="text-primary-400 light:text-primary-700" />
							</linearGradient>
						</defs>
						{[bounds.max, bounds.mid, bounds.min].map((value) => {
							const y = getTrendY(value, bounds);

							return (
								<g key={value}>
									<line
										x1="58"
										y1={y}
										x2="684"
										y2={y}
										className="stroke-neutral-800"
										strokeWidth="1"
										strokeDasharray={value === bounds.min ? undefined : '5 7'}
									/>
									<text x="0" y={y + 4} className="fill-neutral-500 text-[20px] font-medium md:text-[11px]">
										{formatAxisPct(value)}
									</text>
								</g>
							);
						})}

						<path d={toAreaPath(points15, bounds)} fill="url(#salud-trend-fill)" className="text-primary-400 light:text-primary-700" />
						<polyline
							points={toSvgPoints(points15)}
							fill="none"
							className="stroke-primary-400 light:stroke-primary-700"
							strokeWidth="3"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>

						{points15.map((point) => (
							<circle
								key={`15-${point.date}`}
								cx={point.x}
								cy={point.y}
								r={hoveredDate === point.date ? 5 : 3.5}
								className="fill-primary-400 stroke-neutral-925 light:fill-primary-700"
								strokeWidth="2"
							/>
						))}

						{points15.map((point, index) => {
							const left = index === 0 ? TREND_LEFT : (points15[index - 1].x + point.x) / 2;
							const right = index === points15.length - 1 ? TREND_RIGHT : (point.x + points15[index + 1].x) / 2;

							return (
								<rect
									key={`hit-${point.date}`}
									x={left}
									y={TREND_TOP}
									width={Math.max(right - left, 1)}
									height={TREND_BOTTOM - TREND_TOP}
									fill="transparent"
									className="cursor-crosshair"
									onPointerEnter={() => setHoveredDate(point.date)}
								/>
							);
						})}

						{hoveredPoint ? <TrendHoverLabel point={hoveredPoint} /> : <LastPointLabel point={points15[points15.length - 1]} />}

						{xLabels.map((label) => (
							<text key={label.date} x={label.x} y="238" textAnchor={label.anchor} className="fill-neutral-500 text-[20px] font-medium md:text-[11px]">
								{formatShortDate(label.date)}
							</text>
						))}
					</svg>

					<div className="flex flex-wrap gap-4 text-xs text-neutral-400">
						<span className="flex items-center gap-2">
							<span className="h-0.5 w-4 rounded-full bg-primary-400 light:bg-primary-700" />
							Mora 15d
						</span>
						{summary.missingCuts > 0 && <span>{summary.missingCuts} días sin corte en la ventana</span>}
					</div>
				</div>
			) : (
				<div className="flex h-56 flex-col items-center justify-center rounded-xl bg-neutral-900 text-center ring-1 ring-inset ring-white/[0.04]">
					<BarChart3 className="mb-3 size-8 text-neutral-500" />
					<p className="text-sm font-semibold text-neutral-200">Tendencia en construcción</p>
					<p className="mt-1 max-w-sm text-xs text-neutral-500">Se llenará automáticamente con cada corte diario.</p>
				</div>
			)}
		</div>
	);
}

function TrendHoverLabel({ point }: { point: TrendPoint }) {
	const width = 104;
	const height = 38;
	const x = Math.min(Math.max(point.x - width / 2, TREND_LEFT), TREND_RIGHT - width);
	const y = point.y < 60 ? point.y + 14 : point.y - height - 12;

	return (
		<g className="pointer-events-none">
			<line x1={point.x} y1={point.y} x2={point.x} y2={TREND_BOTTOM} className="stroke-neutral-600" strokeWidth="1" strokeDasharray="3 4" />
			<rect x={x} y={y} width={width} height={height} rx="7" className="fill-neutral-800 stroke-neutral-700" strokeWidth="1" />
			<text x={x + 9} y={y + 14} className="fill-neutral-400 text-[9px] font-medium">
				{formatShortDate(point.date)}
			</text>
			<text x={x + 9} y={y + 29} className="fill-neutral-50 text-[12px] font-bold">
				{formatPct(point.value)}
			</text>
		</g>
	);
}

function LastPointLabel({ point }: { point?: TrendPoint }) {
	if (!point) return null;

	return (
		<g transform={`translate(${point.x},${point.y})`}>
			<g className="md:hidden">
				<rect x="-78" y="-44" width="76" height="30" rx="8" className="fill-neutral-100" />
				<text x="-40" y="-23" textAnchor="middle" className="fill-neutral-950 text-[18px] font-bold">
					{formatPct(point.value)}
				</text>
			</g>
			<g className="hidden md:block">
				<rect x="-54" y="-32" width="52" height="20" rx="6" className="fill-neutral-100" />
				<text x="-28" y="-18" textAnchor="middle" className="fill-neutral-950 text-[11px] font-bold">
					{formatPct(point.value)}
				</text>
			</g>
		</g>
	);
}

function TrendStat({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'bad' }) {
	return (
		<div>
			<p className="text-[11px] font-medium text-neutral-500">{label}</p>
			<p
				className={cn(
					'mt-0.5 text-lg font-bold tracking-tight text-neutral-0',
					tone === 'bad' && 'text-amber-400 light:text-amber-700',
					tone === 'good' && 'text-emerald-400 light:text-emerald-700'
				)}
			>
				{value}
			</p>
		</div>
	);
}

function getRecentChanges(health: StoreHealth, history: StoreHealthHistoryPoint[]) {
	const sortedHistory = getSortedHistory(history);
	const currentTimestamp = parseDateTimestamp(health.as_of_date) ?? sortedHistory[sortedHistory.length - 1]?.timestamp ?? null;
	if (currentTimestamp === null) return [];

	return [buildMetricChange('Mora 15d', 'delinquency_15d', health.metrics.delinquency_15d, sortedHistory, currentTimestamp)].filter(
		(change): change is { label: string; current: number; previous: number; delta: number; comparisonLabel: string } => change !== null
	);
}

function buildMetricChange(
	label: string,
	metric: TrendMetric,
	current: number | null,
	history: TimedHealthHistoryPoint[],
	currentTimestamp: number
) {
	if (current === null || current === undefined || !history.length) return null;

	const comparison = getComparisonPoint(history, metric, currentTimestamp, 7) || getComparisonPoint(history, metric, currentTimestamp, 1);
	if (!comparison || comparison.value === current) return null;

	return {
		label,
		current,
		previous: comparison.value,
		delta: current - comparison.value,
		comparisonLabel: comparison.daysAgo === 7 ? 'hace 7d' : `corte ${formatShortDate(comparison.date)}`
	};
}

function getComparisonPoint(history: TimedHealthHistoryPoint[], metric: TrendMetric, currentTimestamp: number, days: number) {
	const targetTimestamp = currentTimestamp - days * TREND_DAY_MS;
	const point = history.find(
		(historyPoint) =>
			historyPoint.timestamp === targetTimestamp && historyPoint.metrics[metric] !== null && historyPoint.metrics[metric] !== undefined
	);
	if (!point) return null;

	return {
		date: point.as_of_date,
		value: Number(point.metrics[metric]),
		daysAgo: days
	};
}

function getBreakdownPriority(row: StoreHealthBreakdown) {
	return (row.delinquency_15d || 0) * Math.log((row.evaluable_15d || 0) + 1);
}

function getBarColorClass(rate: number) {
	if (rate >= 85) return 'bg-red-400 light:bg-red-700';
	if (rate >= 40) return 'bg-orange-400 light:bg-orange-700';
	return 'bg-amber-400 light:bg-amber-700';
}

function BreakdownTable({ rows }: { rows: StoreHealthBreakdown[] }) {
	if (!rows.length) {
		return (
			<div className="mt-5 rounded-xl bg-neutral-900 py-12 text-center text-sm text-neutral-500 ring-1 ring-inset ring-white/[0.04]">
				No hay datos suficientes para este corte.
			</div>
		);
	}

	return (
		<div className="mt-5 min-w-0">
			<div className="grid grid-cols-[24px_minmax(0,1fr)_92px] items-center gap-2 border-b border-neutral-800 pb-3 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-neutral-500 md:grid-cols-[34px_minmax(120px,1.4fr)_minmax(140px,2fr)_120px] md:gap-4 md:px-2">
				<span>#</span>
				<span>Nombre</span>
				<span className="hidden md:block">Mora 15d</span>
				<span className="text-right">Cuotas en mora</span>
			</div>
			{[...rows]
				.sort((a, b) => getBreakdownPriority(b) - getBreakdownPriority(a))
				.map((row, index) => {
					const rate = row.delinquency_15d || 0;

					return (
						<div
							key={row.label}
							className="grid grid-cols-[24px_minmax(0,1fr)_92px] items-center gap-2 border-b border-neutral-800/50 py-3.5 transition-colors last:border-b-0 hover:bg-neutral-900/60 md:grid-cols-[34px_minmax(120px,1.4fr)_minmax(140px,2fr)_120px] md:gap-4 md:px-2"
						>
							<span className="text-xs font-semibold text-neutral-500">{String(index + 1).padStart(2, '0')}</span>
							<div className="min-w-0">
								<p className="truncate text-sm font-semibold text-neutral-100" title={row.label}>
									{row.label}
								</p>
								<p className="mt-0.5 text-[11px] text-neutral-500 md:hidden">{formatPct(row.delinquency_15d)} mora 15d</p>
							</div>
							<div className="hidden items-center gap-3 md:flex">
								<div className="h-2 flex-1 overflow-hidden rounded-full bg-neutral-800">
									<div className={cn('h-full rounded-full', getBarColorClass(rate))} style={{ width: `${Math.max(Math.min(rate, 100), 4)}%` }} />
								</div>
								<span className="w-16 text-right text-sm font-bold text-neutral-100">{formatPct(row.delinquency_15d)}</span>
							</div>
							<span className="text-right text-sm text-neutral-300">
								<b className="font-semibold text-neutral-100">{formatNumber(row.delinquent_15d)}</b> de {formatNumber(row.evaluable_15d)}
							</span>
						</div>
					);
				})}
		</div>
	);
}

function buildFallbackHealth(storeData: Partial<UserData> | null | undefined, storeEmail?: string): StoreHealth | null {
	if (!storeData || !storeEmail) return null;

	const delinquency15d = toNumber((storeData as Partial<UserData> & { delinquency_15?: number }).delinquency_15);
	if (delinquency15d === null) return null;

	return {
		store_email: storeEmail,
		store_name: storeData.store_name,
		country: storeData.country,
		is_legacy_fallback: true,
		metrics: {
			payments_due_count: null,
			unpaid_payments_due_count: null,
			amount_overdue: null,
			evaluable_15d: null,
			delinquent_15d: null,
			delinquency_15d: delinquency15d,
			evaluable_20d: null,
			delinquent_20d: null,
			delinquency_20d: null,
			evaluable_45d: null,
			delinquent_45d: null,
			delinquency_45d: null
		},
		health: {
			status: 'NO_DATA',
			low_sample: true,
			country_thresholds: { low: null, high: null }
		},
		by_model: [],
		by_seller: [],
		by_branch: []
	};
}

function getHealthStatus(health: StoreHealth): HealthStatus {
	if (health.is_legacy_fallback || health.health.country_thresholds?.window_days !== 15) return 'NO_DATA';
	return health.health.status || 'NO_DATA';
}

function toNumber(value: number | string | undefined | null) {
	if (value === undefined || value === null || value === '') return null;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
}

type TimedHealthHistoryPoint = StoreHealthHistoryPoint & { timestamp: number };
type TrendMetric = 'delinquency_15d';
type TrendBounds = { min: number; mid: number; max: number };
type TrendPoint = { x: number; y: number; date: string; value: number };

type TrendSummary = {
	firstDate: string;
	latestDate: string;
	missingCuts: number;
	latest15: number | null;
	delta7d15: number | null;
};

const TREND_DAY_MS = 24 * 60 * 60 * 1000;
const TREND_LEFT = 58;
const TREND_RIGHT = 684;
const TREND_TOP = 18;
const TREND_BOTTOM = 210;

function getSortedHistory(history: StoreHealthHistoryPoint[]) {
	return history
		.map((point) => {
			const timestamp = parseDateTimestamp(point.as_of_date);
			return timestamp === null ? null : { ...point, timestamp };
		})
		.filter((point): point is TimedHealthHistoryPoint => point !== null)
		.sort((a, b) => a.timestamp - b.timestamp);
}

function getFilteredHistory(history: TimedHealthHistoryPoint[], selectedRange: TrendRange) {
	if (!history.length || selectedRange === 'all') return history;

	const latestTimestamp = history[history.length - 1].timestamp;
	const minTimestamp = latestTimestamp - (selectedRange - 1) * TREND_DAY_MS;
	return history.filter((point) => point.timestamp >= minTimestamp);
}

function getTrendBounds(history: TimedHealthHistoryPoint[], metric: TrendMetric): TrendBounds {
	const values = history.map((point) => point.metrics[metric]).filter((value): value is number => value !== null && value !== undefined);
	if (!values.length) return { min: 0, mid: 0.5, max: 1 };

	const rawMin = Math.min(...values);
	const rawMax = Math.max(...values);
	const spread = Math.max(rawMax - rawMin, 0.5);
	const padding = Math.max(spread * 0.25, 0.25);
	const min = Math.max(0, roundAxisValue(rawMin - padding, 'down'));
	const max = Math.max(min + 0.5, roundAxisValue(rawMax + padding, 'up'));

	return { min, mid: (min + max) / 2, max };
}

function buildTimedLinePoints(history: TimedHealthHistoryPoint[], metric: TrendMetric, bounds: TrendBounds): TrendPoint[] {
	if (!history.length) return [];

	const firstTimestamp = history[0].timestamp;
	const lastTimestamp = history[history.length - 1].timestamp;
	const duration = Math.max(lastTimestamp - firstTimestamp, TREND_DAY_MS);

	return history
		.map((point) => {
			const value = point.metrics[metric];
			if (value === null || value === undefined) return null;

			const x = TREND_LEFT + ((point.timestamp - firstTimestamp) / duration) * (TREND_RIGHT - TREND_LEFT);
			const y = getTrendY(value, bounds);
			return { x, y, date: point.as_of_date, value };
		})
		.filter((point): point is TrendPoint => point !== null);
}

function getTrendY(value: number, bounds: TrendBounds) {
	const range = Math.max(bounds.max - bounds.min, 0.5);
	return TREND_TOP + ((bounds.max - value) / range) * (TREND_BOTTOM - TREND_TOP);
}

function roundAxisValue(value: number, direction: 'up' | 'down') {
	const rounded = direction === 'up' ? Math.ceil(value * 10) / 10 : Math.floor(value * 10) / 10;
	return Number(rounded.toFixed(1));
}

function getXAxisLabels(history: TimedHealthHistoryPoint[]) {
	if (!history.length) return [];

	const first = history[0];
	const last = history[history.length - 1];
	const midpoint = history[Math.floor((history.length - 1) / 2)];
	const candidates = [first, midpoint, last];
	const seenDates = new Set<string>();

	return candidates
		.filter((point) => {
			if (seenDates.has(point.as_of_date)) return false;
			seenDates.add(point.as_of_date);
			return true;
		})
		.map((point, index, labels) => ({
			date: point.as_of_date,
			x: getTrendX(point.timestamp, first.timestamp, last.timestamp),
			anchor: index === 0 ? 'start' : index === labels.length - 1 ? 'end' : 'middle'
		}));
}

function getTrendX(timestamp: number, firstTimestamp: number, lastTimestamp: number) {
	const duration = Math.max(lastTimestamp - firstTimestamp, TREND_DAY_MS);
	return TREND_LEFT + ((timestamp - firstTimestamp) / duration) * (TREND_RIGHT - TREND_LEFT);
}

function getTrendSummary(history: TimedHealthHistoryPoint[], comparisonHistory: TimedHealthHistoryPoint[]): TrendSummary | null {
	if (!history.length) return null;

	const first = history[0];
	const latest = history[history.length - 1];
	const daysCovered = Math.round((latest.timestamp - first.timestamp) / TREND_DAY_MS) + 1;

	return {
		firstDate: first.as_of_date,
		latestDate: latest.as_of_date,
		missingCuts: Math.max(0, daysCovered - history.length),
		latest15: latest.metrics.delinquency_15d ?? null,
		delta7d15: getDeltaSince(comparisonHistory, 'delinquency_15d', 7)
	};
}

function getDeltaSince(history: TimedHealthHistoryPoint[], metric: TrendMetric, days: number) {
	if (history.length < 2) return null;

	const latest = history[history.length - 1];
	const latestValue = latest.metrics[metric];
	if (latestValue === null || latestValue === undefined) return null;

	const targetTimestamp = latest.timestamp - days * TREND_DAY_MS;
	const comparison = history.find(
		(point) => point.timestamp === targetTimestamp && point.metrics[metric] !== null && point.metrics[metric] !== undefined
	);

	if (!comparison) return null;
	return latestValue - Number(comparison.metrics[metric]);
}

function toSvgPoints(points: TrendPoint[]) {
	return points.map((point) => `${point.x},${point.y}`).join(' ');
}

function toAreaPath(points: TrendPoint[], bounds: TrendBounds) {
	if (points.length < 2) return '';
	const baseline = getTrendY(bounds.min, bounds);
	const line = points.map((point, index) => `${index ? 'L' : 'M'}${point.x} ${point.y}`).join(' ');
	return `${line} L${points[points.length - 1].x} ${baseline} L${points[0].x} ${baseline} Z`;
}

function parseDateTimestamp(value?: string) {
	if (!value) return null;
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
	if (!match) return null;
	const timestamp = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
	return Number.isFinite(timestamp) ? timestamp : null;
}

function formatPct(value: number | null | undefined) {
	if (value === null || value === undefined) return 'N/A';
	return `${Number(value).toFixed(2)}%`;
}

function formatThreshold(value: number) {
	return Number(value)
		.toFixed(value % 1 === 0 ? 0 : 1)
		.toString();
}

function formatAxisPct(value: number) {
	return `${Number(value).toFixed(value % 1 === 0 ? 0 : 1)}%`;
}

function formatNumber(value: number | null | undefined) {
	if (value === null || value === undefined) return 'N/A';
	return Intl.NumberFormat('es-VE').format(value);
}

function formatDeltaPp(value: number | null) {
	if (value === null) return '—';
	const sign = value > 0 ? '+' : '';
	return `${sign}${value.toFixed(2)} pp`;
}

function getDeltaDetail(delta: number | null, current: number | null) {
	if (delta === null || current === null || current === undefined) return 'Sin cortes suficientes para comparar';
	const previous = current - delta;
	if (delta > 0) return `Empeoró · ${formatPct(previous)} → ${formatPct(current)}`;
	if (delta < 0) return `Mejoró · ${formatPct(previous)} → ${formatPct(current)}`;
	return `Sin cambio · ${formatPct(current)}`;
}

function formatDate(value?: string) {
	if (!value) return 'N/A';
	const timestamp = parseDateTimestamp(value);
	if (timestamp === null) return value;
	return new Date(timestamp).toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
}

function formatShortDate(value?: string) {
	if (!value) return '';
	const timestamp = parseDateTimestamp(value);
	if (timestamp === null) return value;
	return new Date(timestamp).toLocaleDateString('es-VE', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}
